import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { Context } from "@xt/xtbase";
import {
  fetchAllUserCoupons,
  get_user_coupon_by_id,
  resolveCouponTenantId,
} from "@/api/coupon-user";
import { normalizeUserCoupon } from "@/components/Pages/Activity/Voucher/normalizeUserCoupon";
import VoucherClaimModal from "@/components/Pages/Activity/Voucher/VoucherClaimModal";
import {
  extractTemplateCodesFromGrants,
  extractUserCouponIdsFromGrants,
  takeRegisterPendingCouponGrants,
} from "@/utils/registerPendingCouponGrants";
import {
  dismissUnusedCouponHomePromptForever,
  hasUnusedCouponsFromUserInfo,
  markUnusedCouponHomePromptShownToday,
  normalizeUnusedCouponSummaryItem,
  pickUnusedCouponSummaryItem,
  shouldShowUnusedCouponHomePrompt,
} from "@/utils/unusedCouponHomePrompt";

const { XtContext } = Context;

function isMockRegisterCouponPreview(query) {
  const v = query?.mockRegisterCoupon;
  if (v == null) return false;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

function isMockUnusedCouponDailyPreview(query) {
  const v = query?.mockUnusedCouponDaily;
  if (v == null) return false;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

function buildStaticMockCouponDisplay() {
  return normalizeUserCoupon({
    id: "22",
    benefitType: "FIXED_AMOUNT",
    benefitValue: 9900000.4829,
    displayName: "Token Voucher",
    displayDescription: "Token Voucher Voucher",
    status: "AVAILABLE",
    applicableScenes: '["AI_COPYTRADE"]',
    expireAt: "2024-12-24T00:00:00.000Z",
  });
}

/** 注册送券 + 未使用券每日提醒（共用 VoucherClaimModal） */
export default function HomeRegisterCouponModal() {
  const router = useRouter();
  const [appState] = React.useContext(XtContext);
  const { loginStatus, userInfo } = appState;
  const userId = userInfo?.userId;

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [promptMode, setPromptMode] = useState(null);
  const [neverPrompt, setNeverPrompt] = useState(false);

  const isMockPreview = useMemo(
    () => isMockRegisterCouponPreview(router.query),
    [router.query?.mockRegisterCoupon]
  );

  const isMockDailyPreview = useMemo(
    () => isMockUnusedCouponDailyPreview(router.query),
    [router.query?.mockUnusedCouponDaily]
  );

  const tenantId = useMemo(() => resolveCouponTenantId(appState), [appState]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchCouponDetails = useCallback(
    async (pending, { allowStaticFallback = false } = {}) => {
      const grants = pending?.grants;

      if (userId == null || userId === "") {
        return allowStaticFallback ? buildStaticMockCouponDisplay() : null;
      }
      const uid = String(userId);

      const grantList = Array.isArray(grants) ? grants : grants != null ? [grants] : [];
      for (const item of grantList) {
        if (item && typeof item === "object" && (item.benefitType || item.benefit_type || item.status)) {
          const embedded = normalizeUserCoupon(item);
          if (embedded) return embedded;
        }
      }

      const ids = extractUserCouponIdsFromGrants(grants);
      for (const id of ids) {
        try {
          const res = await get_user_coupon_by_id(id, { tenantId, userId: uid });
          if (res?.rc === 0 && res.result) {
            const normalized = normalizeUserCoupon(res.result);
            if (normalized) return normalized;
          }
        } catch (e) {
          console.warn("[HomeRegisterCouponModal] fetch coupon", id, e);
        }
      }

      try {
        const { ok, items } = await fetchAllUserCoupons(
          { tenantId, userId: uid, pageSize: 20, sortBy: "grantedAt", sortOrder: "desc" },
          {}
        );
        if (ok && Array.isArray(items)) {
          const grantIds = extractUserCouponIdsFromGrants(grants);
          const templateCodes = extractTemplateCodesFromGrants(grants);

          const byGrantId = grantIds.length
            ? items.find((row) => grantIds.includes(String(row?.id ?? "")))
            : null;
          if (byGrantId) {
            const normalized = normalizeUserCoupon(byGrantId);
            if (normalized) return normalized;
          }

          const byTemplateCode =
            templateCodes.length > 0
              ? items.find((row) => {
                  const code = row?.templateCode ?? row?.template_code ?? row?.code;
                  return code != null && templateCodes.includes(String(code));
                })
              : null;
          if (byTemplateCode) {
            const normalized = normalizeUserCoupon(byTemplateCode);
            if (normalized) return normalized;
          }

          const registerCoupon = items.find(
            (row) => String(row?.issueSource || row?.issue_source || "").toUpperCase() === "REGISTER"
          );
          if (registerCoupon) {
            const normalized = normalizeUserCoupon(registerCoupon);
            if (normalized) return normalized;
          }
        }
      } catch (e) {
        console.warn("[HomeRegisterCouponModal] fetch coupon list fallback", e);
      }

      return allowStaticFallback ? buildStaticMockCouponDisplay() : null;
    },
    [tenantId, userId]
  );

  const openDailyUnusedCouponPrompt = useCallback(() => {
    const summary = userInfo?.unusedCouponSummary;
    const item = pickUnusedCouponSummaryItem(summary?.items);
    const detail = normalizeUnusedCouponSummaryItem(item);
    if (!detail) return false;
    markUnusedCouponHomePromptShownToday(userId);
    setNeverPrompt(false);
    setPromptMode("daily");
    setCoupon(detail);
    setVisible(true);
    return true;
  }, [userInfo?.unusedCouponSummary, userId]);

  useEffect(() => {
    if (!mounted) return undefined;

    let cancelled = false;

    const run = async () => {
      if (isMockPreview) {
        if (cancelled) return;
        setPromptMode("register");
        setCoupon(buildStaticMockCouponDisplay());
        setVisible(true);
        return;
      }

      if (isMockDailyPreview) {
        if (cancelled) return;
        if (loginStatus !== 1 || !userId) return;
        setPromptMode("daily");
        setNeverPrompt(false);
        setCoupon(buildStaticMockCouponDisplay());
        setVisible(true);
        return;
      }

      if (loginStatus !== 1 || !userId) return;

      const pending = takeRegisterPendingCouponGrants();
      if (pending) {
        const detail = await fetchCouponDetails(pending);
        if (cancelled) return;
        if (detail) {
          setPromptMode("register");
          setCoupon(detail);
          setVisible(true);
        }
        return;
      }

      if (!hasUnusedCouponsFromUserInfo(userInfo)) return;
      if (!shouldShowUnusedCouponHomePrompt(userId)) return;

      if (cancelled) return;
      openDailyUnusedCouponPrompt();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    mounted,
    isMockPreview,
    isMockDailyPreview,
    loginStatus,
    userId,
    userInfo,
    fetchCouponDetails,
    openDailyUnusedCouponPrompt,
  ]);

  const handleClose = ({ neverPrompt: neverPromptOnClose } = {}) => {
    if (promptMode === "daily" && userId && neverPromptOnClose) {
      dismissUnusedCouponHomePromptForever(userId);
    }
    setVisible(false);
    setCoupon(null);
    setPromptMode(null);
    setNeverPrompt(false);
  };

  return (
    <VoucherClaimModal
      visible={visible}
      coupon={coupon}
      onClose={handleClose}
      titleId="home-register-coupon-title"
      showNeverPrompt={promptMode === "daily"}
      neverPrompt={neverPrompt}
      onNeverPromptChange={setNeverPrompt}
    />
  );
}
